import { FC, useState, useEffect } from "react";
import Container from "@/components/Container";
import { ErrorMessage } from "@/components/UI";
import PasswordForm from "@/components/Auth/PasswordForm";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import authService from "@/services/authService";
import ROUTES from "@/data/Routes";
import { useAuth } from "@/data/context/authContext";

enum ResetPasswordType {
  DOCTOR_INVITATION = "doctorInvitation",
  PATIENT = "patient",
}

const ResetPassword: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loadUser } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>();
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const type = searchParams.get("type");
  const isCreatePassword =
    type === ResetPasswordType.DOCTOR_INVITATION ||
    type === ResetPasswordType.PATIENT;

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    const emailParam = searchParams.get("email");

    if (!tokenParam || !emailParam) {
      setErrorMsg(
        isCreatePassword
          ? "Neteisinga nuoroda. Prašome naudoti nuorodą iš el. laiško arba prašyti naujos slaptažodžio sukūrimo nuorodos."
          : "Neteisinga nuoroda. Prašome naudoti nuorodą iš el. laiško arba prašyti naujos slaptažodžio atkūrimo nuorodos.",
      );
      return;
    }

    setToken(tokenParam);
    setEmail(emailParam);
  }, [searchParams, isCreatePassword]);

  const handleSubmit = async (password: string) => {
    if (!token || !email) {
      setErrorMsg("Neteisinga nuoroda");
      return;
    }

    try {
      setIsLoading(true);
      await authService.resetPassword(email, password, password, token);
      if (!authService.getAccessToken()) {
        throw new Error(
          isCreatePassword
            ? "Slaptažodis sukurtas, tačiau prisijungti nepavyko. Bandykite prisijungti rankiniu būdu."
            : "Slaptažodis pakeistas, tačiau prisijungti nepavyko. Bandykite prisijungti rankiniu būdu.",
        );
      }
      await loadUser();
      navigate(ROUTES.ACCOUNT, { replace: true });
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      const err = error as { response?: { data?: { message?: string } } };
      throw new Error(
        err.response?.data?.message ||
          (isCreatePassword
            ? "Klaida kuriant slaptažodį"
            : "Klaida atkuriant slaptažodį"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (errorMsg && (!token || !email)) {
    return (
      <Container>
        <div className="text-grey mx-auto flex min-h-screen flex-col items-center justify-center px-4 md:w-[600px]">
          <ErrorMessage error={errorMsg} />
          {!isCreatePassword && (
            <Link
              to={ROUTES.HOME}
              className="text-primary hover:text-grey-400 mt-6 underline"
            >
              Prašyti naujos slaptažodžio atkūrimo nuorodos
            </Link>
          )}
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="text-grey mx-auto flex min-h-screen flex-col px-4 md:w-[600px]">
        <h1 className="text-primary mt-10 mb-8 text-center">
          {isCreatePassword ? "Sukurti slaptažodį" : "Atkurti slaptažodį"}
        </h1>

        <p className="text-grey-400 mb-8 text-center">
          {isCreatePassword
            ? "Įveskite slaptažodį savo paskyrai."
            : "Įveskite naują slaptažodį savo paskyrai."}
        </p>

        <PasswordForm
          onSubmit={handleSubmit}
          isLoading={isLoading}
          submitButtonLabel={
            isCreatePassword ? "Sukurti slaptažodį" : "Atkurti slaptažodį"
          }
          showLabels={true}
        />
      </div>
    </Container>
  );
};

export default ResetPassword;
